// ============================================================================
// Quota — workspace-scoped performance tracking
// ----------------------------------------------------------------------------
// Soft metric. One active quota per user per workspace at a time. A quota
// holds a `targets` map keyed by target type; a quota is "met" only when
// EVERY entry in the map reaches its targetValue. Progress is denormalized
// on the quota doc — the workspace_activity ledger is the source-of-truth
// backup. See QuotaService for the increment + threshold-coaching flow.
// ============================================================================

import type { Timestamp } from 'firebase/firestore'

export const QUOTA_TARGET_TYPES = ['tasks_completed', 'sales_units', 'checklists_done', 'weighted_progress', 'hours_logged'] as const
export type QuotaTargetType = (typeof QUOTA_TARGET_TYPES)[number]

export type QuotaStatus = 'ACTIVE' | 'ARCHIVED'

export interface QuotaTargetEntry {
  targetValue: number
  currentValue: number
}

export type QuotaTargetMap = Partial<Record<QuotaTargetType, QuotaTargetEntry>>

export interface QuotaCoachingRecord {
  triggeredAt: Timestamp | null
  channel: 'PERFORMANCE_CHANNEL' | 'DM'
  messageRef?: string | null
  triggersDispatched?: string[]
}

export type QuotaCoachingTrigger = 'sixty' | 'eighty' | 'nearMiss' | 'met'

export const DEFAULT_QUOTA_COACHING_TEMPLATES: Record<QuotaCoachingTrigger, string> = {
  sixty: "Hey {name}, you're at {attainment}% attainment. Keep pushing to hit the goal!",
  eighty: "Great progress {name}! You're at {attainment}% attainment, almost there!",
  nearMiss: "Attention {name}: 48 hours left and you're at {attainment}%. Let's finish strong!",
  met: "Congratulations {name}! You have met 100% of your quota!",
}

export const DEFAULT_QUOTA_COACHING_THRESHOLDS = [0.6, 0.8]

export interface QuotaMemberContribution {
  id: string
  quotaId: string
  memberId: string
  delta: {
    tasks_completed?: number
    sales_units?: number
    checklists_done?: number
    weighted_progress?: number
    hours_logged?: number
    customTargets?: Record<string, number>
    [key: string]: unknown
  }
  updatedAt: Timestamp | null
}

export interface QuotaPendingEdit {
  id: string
  workspaceId: string
  quotaId: string
  patch: Partial<Quota>
  requestedById: string
  requestedByName: string
  requestedAt: Timestamp | null
  reviewedById?: string | null
  reviewedByName?: string | null
  reviewedAt?: Timestamp | null
  reviewNote?: string | null
  status: 'pending' | 'approved' | 'rejected'
}

export interface Quota {
  id: string
  workspaceId: string
  assigneeId: string
  targets: QuotaTargetMap
  startDate: Timestamp
  endDate: Timestamp
  status: QuotaStatus
  createdAt: Timestamp
  createdBy: string
  updatedAt: Timestamp
  updatedBy: string
  /** Set on the first 0→100% crossing across all targets. Guards the
   *  celebration trigger against re-firing after edits. */
  celebratedAt?: Timestamp | null
  /** Set when QuotaService dispatches the AI Butler coaching prompt; the
   *  presence of this field is the one-shot-per-period cooldown lock. */
  coaching?: QuotaCoachingRecord | null
  /** Soft-delete tombstone. Archived (auto at endDate) is a separate signal. */
  deletedAt?: Timestamp | null
  templateId?: string | null
  cadence?: 'once' | 'weekly' | 'biweekly' | 'monthly' | 'quarterly'
  teamMemberIds?: string[]
  customTargets?: Record<string, { label: string; targetValue: number; currentValue: number; unit?: string }>
  rewardTiers?: Array<{ percent: number; label: string; badgeUrl?: string | null }>
  notes?: string
  pausedAt?: Timestamp | null
}

export const QUOTA_COLLECTION = 'workspace_quotas'

export const COACHING_THRESHOLD_ATTAINMENT = 0.5
export const COACHING_THRESHOLD_TIME_ELAPSED = 0.5

export const PERFORMANCE_CHANNEL_SLUG = 'performance'

export type QuotaAuditAction =
  | 'quota.set'
  | 'quota.update'
  | 'quota.met'
  | 'quota.archived'
  | 'quota.deleted'
  | 'quota.coaching_sent'
  | 'quota.coaching.dispatched'
  | 'quota.coaching.templates.updated'
  | 'quota.paused'
  | 'quota.resumed'
  | 'quota.edit.request'
  | 'quota.edit.approve'
  | 'quota.edit.reject'
  | 'quota_template.create'
  | 'quota_template.update'
  | 'quota_template.deactivate'
  | 'quota_template.materialise'
  | 'quota_template.pause'
  | 'quota_template.resume'
  | 'quota.reward.grant'

export const isQuotaMet = (quota: Pick<Quota, 'targets'> & Partial<Pick<Quota, 'customTargets'>>): boolean => {
  const entries = Object.values(quota.targets ?? {})
  const hasTargets = entries.length > 0
  const customEntries = Object.values(quota.customTargets ?? {})
  const hasCustomTargets = customEntries.length > 0

  if (!hasTargets && !hasCustomTargets) return false

  const builtInMet = hasTargets ? entries.every((entry) => entry && entry.currentValue >= entry.targetValue) : true
  const customMet = hasCustomTargets ? customEntries.every((entry) => entry && entry.currentValue >= entry.targetValue) : true

  return builtInMet && customMet
}

export const quotaAttainment = (quota: Pick<Quota, 'targets'> & Partial<Pick<Quota, 'customTargets'>>): number => {
  const entries = Object.values(quota.targets ?? {})
  const customEntries = Object.values(quota.customTargets ?? {})
  const totalEntries = entries.length + customEntries.length

  if (totalEntries === 0) return 0

  let sum = 0
  for (const entry of entries) {
    if (!entry || entry.targetValue <= 0) continue
    sum += Math.min(1, entry.currentValue / entry.targetValue)
  }
  for (const entry of customEntries) {
    if (!entry || entry.targetValue <= 0) continue
    sum += Math.min(1, entry.currentValue / entry.targetValue)
  }
  return sum / totalEntries
}
